import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Eye, EyeOff, ShieldCheck, TrendingUp, Sparkles, ArrowRight, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { InstallPwaButton } from '@/components/common/InstallPwaButton';
import { SplashScreen } from '@/components/common/SplashScreen';

const ROTATING_LINES = [
  { icon: TrendingUp, k: 'Trading Signals', v: 'Timely entry, exit and stop-loss levels' },
  { icon: Sparkles, k: 'Market Advisory', v: 'Research-backed views on key markets' },
  { icon: Zap, k: 'Instant Alerts', v: 'Real-time notifications on your phone' },
  { icon: ShieldCheck, k: 'Secure Access', v: 'Encrypted, role-based sign-in' },
];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rotateIdx, setRotateIdx] = useState(0);
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const t = setInterval(() => setRotateIdx(i => (i + 1) % ROTATING_LINES.length), 3800);
    return () => clearInterval(t);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.requires_verification) {
        toast.info('Account not verified. Please check your email.');
        return;
      }
      if (data.access_token) {
        toast.success('Welcome back');
        await checkAuth();
        const role = data.user?.role;
        if (role === 'student') navigate('/student/portal');
        else if (role === 'investor') navigate('/investor');
        else if (role === 'sales_person') navigate('/sales');
        else navigate('/dashboard');
      }
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const RotLine = ROTATING_LINES[rotateIdx];
  const RotIcon = RotLine.icon;

  return (
    <div className="min-h-screen flex bg-[#0A0E1A] text-white overflow-hidden" data-testid="login-page">
      {/* ───────────────────────── LEFT — Cinematic dark hero ───────────────────────── */}
      <div className="hidden lg:flex lg:w-[58%] relative overflow-hidden">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#050914] via-[#0A0E1A] to-[#0F1A2E]" />

        {/* Radial spot lights */}
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-emerald-500/15 blur-[120px]" />
        <div className="absolute top-1/3 right-0 w-[420px] h-[420px] rounded-full bg-sky-500/10 blur-[120px]" />
        <div className="absolute -bottom-40 left-1/4 w-[480px] h-[480px] rounded-full bg-emerald-400/10 blur-[140px]" />

        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(circle at 50% 50%, black 40%, transparent 78%)',
          }}
        />

        {/* Subtle noise */}
        <div className="absolute inset-0 opacity-[0.035] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")' }}
        />

        {/* Animated candlestick chart art */}
        <svg viewBox="0 0 800 460" className="absolute bottom-[8%] left-0 right-0 w-full h-[52%] opacity-90" preserveAspectRatio="none">
          <defs>
            <linearGradient id="ln" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#34D399" stopOpacity="0" />
              <stop offset="50%" stopColor="#34D399" stopOpacity="1" />
              <stop offset="100%" stopColor="#34D399" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="fillArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#10B981" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Area under curve */}
          <path
            d="M0,340 C90,300 140,320 210,270 C280,220 340,255 420,190 C500,125 570,175 640,120 C700,75 750,110 800,80 L800,460 L0,460 Z"
            fill="url(#fillArea)"
          />
          {/* Trend line */}
          <path
            d="M0,340 C90,300 140,320 210,270 C280,220 340,255 420,190 C500,125 570,175 640,120 C700,75 750,110 800,80"
            fill="none" stroke="url(#ln)" strokeWidth="2.2"
            style={{ filter: 'drop-shadow(0 0 8px rgba(52,211,153,0.6))' }}
          />

          {/* Candlesticks */}
          {[
            [70, 320, 350, 290, 335, '#10B981'], [110, 300, 340, 270, 315, '#10B981'],
            [150, 315, 360, 285, 300, '#F43F5E'], [190, 285, 320, 250, 275, '#10B981'],
            [230, 270, 310, 240, 260, '#10B981'], [270, 275, 305, 235, 245, '#10B981'],
            [310, 250, 280, 210, 245, '#F43F5E'], [350, 235, 265, 205, 220, '#10B981'],
            [390, 210, 245, 180, 200, '#10B981'], [430, 200, 230, 160, 180, '#10B981'],
            [470, 195, 220, 155, 175, '#F43F5E'], [510, 175, 205, 140, 155, '#10B981'],
            [550, 150, 185, 120, 138, '#10B981'], [590, 140, 170, 105, 125, '#10B981'],
            [630, 125, 155, 90, 112, '#10B981'], [670, 115, 145, 80, 100, '#F43F5E'],
            [710, 100, 135, 70, 88, '#10B981'], [750, 90, 118, 60, 78, '#10B981'],
          ].map(([x, h, l, hh, bo, col]) => (
            <g key={x}>
              <line x1={x} y1={hh} x2={x} y2={l} stroke={col} strokeWidth="1.5" opacity="0.9" />
              <rect x={x - 6} y={Math.min(h, bo)} width="12" height={Math.abs(bo - h)} fill={col} opacity="0.95" rx="1" />
            </g>
          ))}

          {/* Latest price dot */}
          <circle cx="750" cy="88" r="6" fill="#34D399" style={{ filter: 'drop-shadow(0 0 12px rgba(52,211,153,0.9))' }}>
            <animate attributeName="r" values="4;7;4" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.7;1;0.7" dur="2s" repeatCount="indefinite" />
          </circle>
        </svg>

        {/* Floating orbs */}
        <div className="absolute top-24 right-24 w-3 h-3 rounded-full bg-emerald-400 shadow-[0_0_24px_rgba(52,211,153,0.9)] animate-pulse" />
        <div className="absolute top-64 left-32 w-2 h-2 rounded-full bg-sky-400 shadow-[0_0_20px_rgba(56,189,248,0.9)] animate-pulse" style={{ animationDelay: '1.2s' }} />
        <div className="absolute bottom-32 right-40 w-2.5 h-2.5 rounded-full bg-emerald-300 shadow-[0_0_20px_rgba(110,231,183,0.8)] animate-pulse" style={{ animationDelay: '0.6s' }} />

        {/* Content over the hero */}
        <div className="relative z-10 flex flex-col justify-between w-full p-12 xl:p-16">
          {/* Top: brand + status pill */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img src="/quantora-logo.png" alt="Quantora" className="w-11 h-11 rounded-xl ring-1 ring-white/10" />
                <div className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-[#0A0E1A]" />
              </div>
              <div>
                <p className="font-heading text-lg font-bold tracking-tight leading-none">QUANTORA</p>
                <p className="text-[10px] text-emerald-300/80 tracking-[0.28em] uppercase mt-1">Market Advisory</p>
              </div>
            </div>

            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.06] backdrop-blur-md border border-white/10 text-[11px]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="text-white/80 font-medium tracking-wide">Markets Open</span>
            </div>
          </div>

          {/* Middle: headline + rotating value prop */}
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/10 border border-emerald-400/20 text-emerald-300 text-[10px] font-semibold tracking-[0.25em] uppercase mb-6">
              <Sparkles size={11} /> Sign in to your desk
            </div>
            <h1 className="font-heading text-[52px] xl:text-6xl font-black leading-[1.02] tracking-tight">
              Read the market
              <br />
              <span className="bg-gradient-to-r from-emerald-300 via-emerald-400 to-cyan-300 bg-clip-text text-transparent">
                with clarity.
              </span>
            </h1>
            <p className="mt-5 text-white/60 text-[15px] leading-relaxed max-w-md">
              Quantora delivers timely trading signals and market advisory — research-backed insights to help you make your own informed decisions.
            </p>

            {/* Rotating feature strip */}
            <div className="mt-8 h-14 relative overflow-hidden">
              <div key={rotateIdx} className="flex items-center gap-4 animate-[fadeSlide_0.5s_ease-out]">
                <div className="w-10 h-10 rounded-lg bg-white/[0.06] border border-white/10 flex items-center justify-center">
                  <RotIcon size={18} className="text-emerald-300" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{RotLine.k}</p>
                  <p className="text-xs text-white/50">{RotLine.v}</p>
                </div>
              </div>
              {/* dots */}
              <div className="absolute bottom-0 left-14 flex gap-1.5">
                {ROTATING_LINES.map((line, i) => (
                  <button key={line.k} onClick={() => setRotateIdx(i)} aria-label={`Feature ${i + 1}`}
                    className={`h-1 rounded-full transition-all ${i === rotateIdx ? 'w-6 bg-emerald-400' : 'w-1.5 bg-white/25 hover:bg-white/50'}`} />
                ))}
              </div>
            </div>
          </div>

          {/* Bottom: trust bar */}
          <div className="flex items-center justify-between text-[11px] text-white/40">
            <div className="flex items-center gap-5">
              <span className="flex items-center gap-1.5"><ShieldCheck size={12} className="text-emerald-400" /> 256-bit encrypted</span>
            </div>
            <span className="tracking-[0.2em] text-white/50">QUANTORA</span>
          </div>
        </div>
      </div>

      {/* ───────────────────────── RIGHT — Clean form ───────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-white text-slate-900 relative">
        {/* Corner accent */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-emerald-100 to-transparent rounded-bl-[4rem] opacity-70 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-sky-50 to-transparent rounded-tr-[3rem] opacity-70 pointer-events-none" />

        <div className="w-full max-w-md relative z-10">
          {/* Mobile brand — richer */}
          <div className="lg:hidden mb-7">
            <div className="flex items-center gap-3 mb-4">
              <img src="/quantora-logo.png" alt="Quantora" className="w-14 h-14 object-contain" />
              <div>
                <p className="font-heading text-lg font-black tracking-tight">QUANTORA</p>
                <p className="text-[10px] text-emerald-700 tracking-[0.2em] uppercase font-semibold">Market Advisory</p>
              </div>
            </div>
            {/* Mobile install prompt */}
            <InstallPwaButton className="mb-3" />
          </div>

          {/* Heading */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold text-emerald-700 tracking-[0.28em] uppercase mb-3">
              <span className="w-6 h-px bg-emerald-500" /> Sign In
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight leading-tight">
              Welcome back
            </h2>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              Sign in to your Quantora account.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4" noValidate>
            {error && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm flex items-start gap-2" data-testid="login-error">
                <span className="w-1 h-full bg-rose-500 rounded-full" />
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Work Email</Label>
              <Input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@quantora.co.in" required
                data-testid="login-email-input"
                className="bg-slate-50 border-slate-200 rounded-xl h-12 text-sm focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:border-emerald-400 transition-all"
                autoComplete="email"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-500">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••••" required
                  data-testid="login-password-input"
                  className="bg-slate-50 border-slate-200 rounded-xl h-12 text-sm pr-11 focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:border-emerald-400 transition-all"
                  autoComplete="current-password"
                />
                <button
                  type="button" onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                  data-testid="toggle-password-visibility" tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input type="checkbox" className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" />
                <span className="text-xs text-slate-600 group-hover:text-slate-900 transition-colors">Keep me signed in</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit" disabled={loading}
              className="group w-full h-12 mt-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-semibold shadow-lg shadow-slate-900/25 hover:shadow-slate-900/40 hover:-translate-y-[1px] transition-all disabled:hover:translate-y-0"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Sign in
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </span>
              )}
            </Button>
          </form>

          {/* Divider */}
          <p className="mt-8 text-center text-[11px] text-slate-400 tracking-[0.2em]" data-testid="login-footer">
            © {new Date().getFullYear()} QUANTORA
          </p>
        </div>
      </div>

      {/* Component-scoped keyframes for the rotating feature strip */}
      <style>{`
        @keyframes fadeSlide {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
